/**
 * @fileoverview accounts 应用日志入口（薄封装层）
 *
 * 日志采集逻辑已迁入 @peerup/browser-logger。本文件仅做三件事：
 * 1. re-export 包的 core / types，保持业务 import 路径不变
 * 2. 合并导出 accounts 的枚举（公共 + 私有，见 ./types）
 * 3. 用 createLogger 创建 accounts 实例，支持 micro-app 注入（复用主应用 logger）
 *
 * @logPage [dev日志查看地址](https://sls.console.aliyun.com/lognext/project/proj-rum-546ff24f3416ee69647768cb50b01c/logstore-fe-dev?slsRegion=cn-hangzhou)
 * @logPage [prod日志查看地址](https://sls.console.aliyun.com/lognext/project/proj-rum-546ff24f3416ee69647768cb50b01c/logstore-fe-prod?slsRegion=cn-hangzhou)
 * @docs [WebTracking 官方文档](https://help.aliyun.com/zh/sls/developer-reference/use-web-tracking-sdk-for-javascript-to-collect-browser-logs)
 */
import type { AliWebLogger } from "@peerup/browser-logger"

import { createLogger } from "@peerup/browser-logger"
import { deepToRaw } from "@peerup/helper"

import { allowLog, envConfig, getGlobalData, IS_DEV, VITE_APP_VERSION } from "@/config"

import type { ErrorEventMap, InfoEventMap, WarnEventMap } from "./types"

export * from "./types"
// ===== re-export 包的能力（业务 import 路径不变） =====
export { AliWebLogger, createLogger } from "@peerup/browser-logger"

// ===== 创建 accounts 实例 =====
/**
 * 全局日志对象，支持四种日志级别：log(普通)、warn(警告)、error(错误)、info(埋点信息)
 *
 * micro-app 场景：若作为子应用运行，优先复用主应用注入的 logger（externalInstance），
 * 避免重复实例化/重复上报；否则在 accounts 内用 createLogger 新建。
 */
const data = getGlobalData()

export const $logger = createLogger<WarnEventMap, ErrorEventMap, InfoEventMap>({
  appVersion: VITE_APP_VERSION,
  env: envConfig.VITE_APP_ENV,
  staticUrl: envConfig.STATIC_URL,
  isDev: IS_DEV,
  allowLog,
  transformData: data => deepToRaw(data, { warnInConsole: true }),
  sls: {
    source: "accounts",
    topic: "peerup-fe",
    tracker: envConfig.TRACKER_CONFIG,
    innerLoggerEnable: envConfig.INNER_LOGGER_ENABLE,
  },
  // authKit：等 isReady 后取 userInfo（提取 BaseUserInfo 所需字段）
  authKitPromise: async () => {
    try {
      const { $authKit } = await import("@/store/modules/auth")
      await $authKit?.isReady()
      const info = $authKit?.userInfo
      if (!info) return null
      return {
        userId: info.userId,
        memberGuid: info.memberGuid,
        orgGuid: info.orgGuid,
        userName: info.userName,
      }
    } catch {
      return null
    }
  },
  plugins: {
    localLogger: true,
  },
  // micro-app 注入：复用主应用 logger（data.logger 类型为主应用实例，运行时兼容 AliWebLogger）
  externalInstance: data?.logger as AliWebLogger | undefined,
})

export default $logger
